/**
 * ScrollOnePager — main one-pager with Lenis smooth scroll.
 *
 * Body scroll (window) replaces the old custom overflow-y-scroll div.
 * Lenis smooths the scroll and feeds velocity to the 3D wall layer.
 *
 * Section tracking uses IntersectionObserver (not GSAP ScrollTrigger) so that
 * it observes live element positions and is immune to document-position shifts
 * caused by HorizontalGallery's sticky layout changes.
 *
 * Sections: HomeSection → ProjectSection × N → ContactSection
 * Each ProjectSection contains its own ProjectInfoBlock + HorizontalGallery.
 */
import Lenis from "lenis"
import gsap from "gsap"
import { ScrollToPlugin } from "gsap/ScrollToPlugin"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import { ContactSection } from "@/components/sections/ContactSection"
import { HomeSection } from "@/components/sections/HomeSection"
import { ProjectSection } from "@/components/sections/ProjectSection"
import { UnifiedCarousel } from "@/components/sections/UnifiedCarousel"
import { useProjects } from "@/hooks/useProjects"
import { useIsDesktop } from "@/hooks/useIsDesktop"
import { useSectionScrollStore, type SectionDescriptor } from "@/stores/sectionScrollStore"
import { useTransitionStore } from "@/stores/transitionStore"
import { setGlobalLenis, globalLenis } from "@/lib/lenis"
import { useEffect, useRef, useCallback, useMemo } from "react"
import { useLocation } from "wouter"

gsap.registerPlugin(ScrollToPlugin, ScrollTrigger)

function buildSections(
  projects: { id: string; title: string | null; images: { id: string }[] }[],
): SectionDescriptor[] {
  return [
    { id: "home", route: "/home", label: "Home", type: "home" },
    ...projects.map((p) => ({
      id: `project-${p.id}`,
      route: `/project/${p.id}`,
      label: p.title ?? "Project",
      type: "project" as const,
      projectId: p.id,
      imageCount: p.images.length,
    })),
    { id: "contact", route: "/contact", label: "Contact", type: "contact" },
  ]
}

function getInitialSectionIndex(
  path: string,
  projects: { id: string }[],
): number | null {
  if (path === "/home") return 0
  if (path === "/contact") return projects.length + 1
  if (path.startsWith("/project/")) {
    const id = path.split("/")[2]
    const idx = projects.findIndex((project) => project.id === id)
    return idx === -1 ? null : 1 + idx
  }
  return 0
}

export function ScrollOnePager() {
  const [pathname] = useLocation()

  const isDesktop = useIsDesktop()
  const isDesktopRef = useRef(isDesktop)
  useEffect(() => { isDesktopRef.current = isDesktop }, [isDesktop])

  const { data: projects, isPending } = useProjects()
  const setSections            = useSectionScrollStore((s) => s.setSections)
  const setActiveSectionIndex  = useSectionScrollStore((s) => s.setActiveSectionIndex)
  const setWallScrollVelocity  = useSectionScrollStore((s) => s.setWallScrollVelocity)
  const activeSectionIndex     = useSectionScrollStore((s) => s.activeSectionIndex)
  const setCurrentPath        = useTransitionStore((s) => s.setCurrentPath)

  const homeSectionRef    = useRef<HTMLElement>(null)
  const contactSectionRef = useRef<HTMLElement>(null)
  const projectSectionRefs = useRef<Map<string, HTMLElement>>(new Map())

  const currentPathRef = useRef(typeof window !== "undefined" ? window.location.pathname : "/home")
  const initialPathRef = useRef(typeof window !== "undefined" ? window.location.pathname : "/home")
  const isFirstRenderRef = useRef(true)
  const hasHydratedInitialRouteRef = useRef(false)
  const isRestoringInitialRouteRef = useRef(false)
  const initialRestoreTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const activeSectionIndexRef = useRef(0)
  const isProgrammaticDesktopProjectNavRef = useRef(false)

  // Exposed by UnifiedCarousel after mount; used for desktop nav
  const getScrollYForProjectRef = useRef<((projectIndex: number) => number) | null>(null)

  const resolvedProjects = useMemo(() => projects ?? [], [projects])
  const initialSectionIndex = useMemo(
    () => getInitialSectionIndex(initialPathRef.current, resolvedProjects),
    [resolvedProjects],
  )
  const resolvedProjectsRef = useRef(resolvedProjects)
  useEffect(() => { resolvedProjectsRef.current = resolvedProjects }, [resolvedProjects])
  useEffect(() => { activeSectionIndexRef.current = activeSectionIndex }, [activeSectionIndex])

  // ── URL sync: update route whenever active section changes ────────────────
  // Covers all section types — project sections on desktop are driven by the
  // carousel's setActiveSectionIndex and never go through onSectionActive.
  // history.replaceState does not trigger wouter's pathname effect so there
  // is no circular scroll.
  const sectionsForUrl = useSectionScrollStore((s) => s.sections)
  useEffect(() => {
    if (!sectionsForUrl.length) return
    const section = sectionsForUrl[activeSectionIndex]
    if (!section) return

    if (!hasHydratedInitialRouteRef.current) {
      if (initialSectionIndex === null || activeSectionIndex !== initialSectionIndex) return
      hasHydratedInitialRouteRef.current = true
      currentPathRef.current = initialPathRef.current
      setCurrentPath(initialPathRef.current)
      return
    }

    if (section.route === currentPathRef.current) return
    history.replaceState(null, "", section.route)
    setCurrentPath(section.route)
    currentPathRef.current = section.route
  }, [activeSectionIndex, initialSectionIndex, sectionsForUrl, setCurrentPath])

  // ── Build sections list ───────────────────────────────────────────────────
  useEffect(() => {
    if (!projects) return
    setSections(buildSections(projects))
  }, [projects, setSections])

  // ── Project section ref callback ──────────────────────────────────────────
  const setProjectRef = useCallback((projectId: string, el: HTMLElement | null) => {
    if (el) projectSectionRefs.current.set(projectId, el)
    else    projectSectionRefs.current.delete(projectId)
  }, [])


  // ── Lenis smooth scroll ───────────────────────────────────────────────────
  //
  // Lenis is the only scroll authority here. Route changes already update the
  // URL as sections become active, so adding a second gesture-end snap causes
  // visible correction jumps after the user has already entered the next
  // section.
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      touchMultiplier: 2,
    })

    setGlobalLenis(lenis)

    lenis.on("scroll", ({ velocity }: { velocity: number }) => {
      setWallScrollVelocity(Math.max(-1, Math.min(1, velocity / 1200)))
    })

    // Wire Lenis into GSAP ticker so ScrollTrigger scrubs stay in sync
    lenis.on("scroll", ScrollTrigger.update)
    gsap.ticker.add((time) => lenis.raf(time * 1000))
    gsap.ticker.lagSmoothing(0)

    return () => {
      lenis.destroy()
      setGlobalLenis(null)
      gsap.ticker.lagSmoothing(100, 16)
    }
  }, [setWallScrollVelocity])

  // ── React Router navigation → scroll to matching section ─────────────────
  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false
      return
    }

    const path     = pathname
    const projects = resolvedProjectsRef.current

    // Home and contact always scroll unconditionally — IO fires for home at
    // scroll=0 on load, which sets currentPathRef.current to "/home" before
    // the user can click; skipping the check ensures re-clicking still works.
    if (path === "/home") {
      currentPathRef.current = path
      setActiveSectionIndex(0)
      globalLenis?.scrollTo(homeSectionRef.current?.offsetTop ?? 0, { immediate: true })
      return
    }
    if (path === "/contact") {
      currentPathRef.current = path
      setActiveSectionIndex(projects.length + 1)
      if (contactSectionRef.current) {
        globalLenis?.scrollTo(contactSectionRef.current.offsetTop, { immediate: true })
      }
      return
    }

    if (pathname === currentPathRef.current) return

    let targetIndex: number | null = null
    let targetEl: HTMLElement | null = null

    if (path.startsWith("/project/")) {
      const id  = path.split("/")[2]
      const idx = projects.findIndex((p) => p.id === id)
      if (idx !== -1) {
        const resolvedTargetIndex = 1 + idx
        targetIndex = resolvedTargetIndex
        if (isDesktopRef.current && getScrollYForProjectRef.current) {
          const y = getScrollYForProjectRef.current(idx)
          currentPathRef.current = path
          isProgrammaticDesktopProjectNavRef.current = true
          setActiveSectionIndex(resolvedTargetIndex)
          globalLenis?.scrollTo(y, { immediate: true })
          // scrub: 0.8 needs ~1s to settle after the snap
          gsap.delayedCall(1.0, () => {
            isProgrammaticDesktopProjectNavRef.current = false
          })
          return
        }
        targetEl = projectSectionRefs.current.get(id) ?? null
      }
    }

    if (targetIndex !== null) setActiveSectionIndex(targetIndex)

    if (targetEl) {
      currentPathRef.current = path
      globalLenis?.scrollTo(targetEl.offsetTop, { immediate: true })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  // ── IntersectionObserver section tracking ─────────────────────────────────
  //
  // rootMargin "-45% 0px -55% 0px" creates a zero-height detection line at
  // 45% from the viewport top. A section becomes "active" when it straddles
  // that line. IO always observes live element positions, so it is immune to
  // document shifts caused by the gallery's sticky layout.
  useEffect(() => {
    if (isPending) return

    const sections = buildSections(resolvedProjects)
    const els: Array<{ el: HTMLElement; section: SectionDescriptor; idx: number }> = []

    sections.forEach((section, i) => {
      let el: HTMLElement | null = null
      if (section.type === "home")         el = homeSectionRef.current
      else if (section.type === "contact") el = contactSectionRef.current
      // On desktop, UnifiedCarousel tracks project sections internally via Swiper active index
      else if (!isDesktop && section.projectId)
        el = projectSectionRefs.current.get(section.projectId) ?? null
      if (el) els.push({ el, section, idx: i })
    })

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue
          const found = els.find(({ el }) => el === entry.target)
          if (found) onSectionActive(found.section, found.idx)
        }
      },
      { rootMargin: "-45% 0px -55% 0px", threshold: 0 },
    )

    els.forEach(({ el }) => io.observe(el))

    // Initial scroll position
    const initialIndex = getInitialSectionIndex(currentPathRef.current, resolvedProjects) ?? 0

    if (initialIndex > 0) {
      isRestoringInitialRouteRef.current = true
      if (initialRestoreTimerRef.current) clearTimeout(initialRestoreTimerRef.current)
      initialRestoreTimerRef.current = setTimeout(() => {
        isRestoringInitialRouteRef.current = false
        initialRestoreTimerRef.current = null
      }, 500)
    } else {
      isRestoringInitialRouteRef.current = false
    }

    setActiveSectionIndex(initialIndex)

    if (initialIndex > 0) {
      const targetSection = sections[initialIndex]
      let targetY: number | null = null
      if (targetSection?.type === "contact") {
        targetY = contactSectionRef.current?.offsetTop ?? null
      } else if (targetSection?.type === "project") {
        if (isDesktop) {
          // Desktop: UnifiedCarousel handles initial scroll via initialProjectIndex prop.
          // Nothing to do here — getScrollYForProject may not be ready yet.
        } else if (targetSection.projectId) {
          targetY = projectSectionRefs.current.get(targetSection.projectId)?.offsetTop ?? null
        }
      }
      if (targetY !== null) globalLenis?.scrollTo(targetY, { immediate: true })
    }

    return () => {
      io.disconnect()
      if (initialRestoreTimerRef.current) {
        clearTimeout(initialRestoreTimerRef.current)
        initialRestoreTimerRef.current = null
      }
      isRestoringInitialRouteRef.current = false
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPending, resolvedProjects.length])

  function onSectionActive(_section: SectionDescriptor, index: number) {
    if (isRestoringInitialRouteRef.current) return
    setActiveSectionIndex(index)
  }

  if (isPending) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <div className="h-1 w-24 overflow-hidden rounded-full bg-white/10">
          <div className="h-full animate-pulse rounded-full bg-white/30" />
        </div>
      </div>
    )
  }

  return (
    <div className="pointer-events-auto">
      <HomeSection ref={homeSectionRef} />

      {isDesktop ? (
        <div key="desktop-layout" data-scroll-layout="desktop">
          <UnifiedCarousel
            projects={resolvedProjects}
            initialProjectIndex={initialSectionIndex !== null && initialSectionIndex > 0
              ? initialSectionIndex - 1
              : null}
            onReady={(fn) => { getScrollYForProjectRef.current = fn }}
            suppressActiveProjectSyncRef={isProgrammaticDesktopProjectNavRef}
          />
        </div>
      ) : (
        <div key="mobile-layout" data-scroll-layout="mobile">
          {resolvedProjects.map((project, i) => (
            <ProjectSection
              key={project.id}
              ref={(el) => setProjectRef(project.id, el)}
              project={project}
              sectionIndex={1 + i}
            />
          ))}
        </div>
      )}

      <ContactSection ref={contactSectionRef} />
    </div>
  )
}
